// What a stashed link is, from its URL, which decides how the lists draw it: a video gets a card
// with a big picture (you pick a video by its thumbnail), a post on X shows the post itself, and
// everything else reads like an article. A port of the iPhone app's ItemKind
// (apps/ios/Packages/StashItKit/Sources/StashItCore/ItemKind.swift); keep the two in step.

export type ItemKind =
  | { kind: "video"; youTubeId: string | null }
  | { kind: "xPost" }
  | { kind: "article" };

/** Sites whose links are videos, matched on the domain and its subdomains. */
const videoSites = ["vimeo.com", "twitch.tv", "tiktok.com", "dailymotion.com", "loom.com"];
const youTubeDomains = ["youtube.com", "youtu.be", "youtube-nocookie.com"];
const xHosts = ["x.com", "twitter.com"];

const parseUrl = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

/** The domain is one of these, or a subdomain of one. */
const matches = (domain: string, sites: string[]) => {
  const d = domain.toLowerCase();
  return sites.some((site) => d == site || d.endsWith("." + site));
};

export const itemKind = (url: string): ItemKind => {
  const youTubeId = youTubeVideoId(url);
  if (youTubeId) return { kind: "video", youTubeId };
  if (isXPost(url)) return { kind: "xPost" };
  if (matches(parseUrl(url)?.hostname ?? "", videoSites)) return { kind: "video", youTubeId: null };
  return { kind: "article" };
};

/**
 * A video's ID from any of the shapes its links come in: `watch?v=`, `youtu.be/`, `shorts/`,
 * `embed/`, `live/` and `v/`. Null for anything else, YouTube's other pages included.
 */
export const youTubeVideoId = (url: string): string | null => {
  const parsed = parseUrl(url);
  if (!parsed) return null;
  const host = parsed.hostname.toLowerCase();
  if (!matches(host, youTubeDomains)) return null;
  const path = parsed.pathname.split("/").filter(Boolean);
  let candidate: string | null | undefined = null;
  if (host.endsWith("youtu.be")) candidate = path[0];
  else if (path[0] == "watch") candidate = parsed.searchParams.get("v");
  else if (path.length >= 2 && ["shorts", "embed", "live", "v"].includes(path[0] ?? ""))
    candidate = path[1];
  if (!candidate || !/^[A-Za-z0-9_-]{11}$/.test(candidate)) return null;
  return candidate;
};

/** Every video has this one; the bigger maxres one isn't always made. */
export const youTubeThumbnailUrl = (id: string) => `https://i.ytimg.com/vi/${id}/hqdefault.jpg`;

// ---- Posts on X ----

export interface XPost {
  /** "The BowTied AI" */
  name: string;
  /** "@thebowtiedai", from the link */
  handle: string | null;
  /** What they said, without the link to its picture X tacks on the end */
  text: string;
}

const statusPath = (url: string): { handle: string | null } | null => {
  const parsed = parseUrl(url);
  if (!parsed || !matches(parsed.hostname, xHosts)) return null;
  const path = parsed.pathname.split("/").filter(Boolean);
  const status = path.indexOf("status");
  const id = path[status + 1];
  if (status < 0 || !id || !/^\d+$/.test(id)) return null;
  // `x.com/i/web/status/<id>` has no handle in it
  return { handle: status == 1 && path[0] != "i" ? (path[0] ?? null) : null };
};

/** A link to a post (`x.com/<handle>/status/<id>`), not a profile or anything else on X. */
export const isXPost = (url: string) => statusPath(url) != null;

const xTitlePattern = /^(.+?) on (?:X|Twitter): ["“]([\s\S]*)["”]\s*\/\s*(?:X|Twitter)$/;

/**
 * The post, from its stashed title and link. Null when the title isn't X's post title (it hasn't
 * been fetched yet, or it's a profile's "Name (@handle) on X"), so the list shows the title as it is.
 */
export const parseXPost = (title: string | null, url: string): XPost | null => {
  const path = statusPath(url);
  if (!title || !path) return null;
  const match = xTitlePattern.exec(title.trim());
  if (!match) return null;
  // Some titles carry the handle too: "Name (@handle) on X: …"
  const name = (match[1] ?? "").replace(/\s*\(@\w+\)$/, "").trim();
  // X ends a post with a picture with a t.co link to it, which means nothing in a list
  const text = (match[2] ?? "").replace(/\s*https:\/\/t\.co\/\w+\s*$/, "").trim();
  if (!name || !text) return null;
  return { name, handle: path.handle ? "@" + path.handle : null, text };
};
